import { useEffect, useState } from 'react';
import axios from 'axios';
import { appConfig } from '../../../constants';
import { mergeResultsWithAnswerSheet } from '../utils/mergePdfs';

const isPreSignedUrl = (url) =>
  Boolean(url && (url.includes('X-Amz-') || url.includes('cloudflarestorage')));

const looksLikePdf = (buffer) => {
  if (!buffer || buffer.byteLength < 5) return false;
  const header = new Uint8Array(buffer.slice(0, 5));
  return String.fromCharCode(...header) === '%PDF-';
};

const toArrayBuffer = (data) => {
  if (!data) return null;
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  }
  return null;
};

const getFileDownloadUrl = (pdfKey) => {
  if (!pdfKey) return null;
  const base = String(appConfig.apiBaseUrl || '').replace(/\/?$/, '/');
  return `${base}file/download?key=${encodeURIComponent(pdfKey)}&action=view`;
};

const getResultsSheetUrl = (studentAssignmentId) => {
  if (!studentAssignmentId) return null;
  const base = String(appConfig.apiBaseUrl || '').replace(/\/?$/, '/');
  return `${base}marksheets/student/${studentAssignmentId}/results-pdf`;
};

const fetchPdfBytes = async (url, token) => {
  if (!url) return null;
  const headers = isPreSignedUrl(url)
    ? {}
    : token
      ? { Authorization: `Bearer ${token}` }
      : {};

  const response = await axios.get(url, {
    headers,
    responseType: 'arraybuffer',
    // Follow redirects in Node; browsers always follow. Validate PDF after.
    validateStatus: (status) => status >= 200 && status < 400
  });

  const buffer = toArrayBuffer(response.data);
  if (!looksLikePdf(buffer)) {
    throw new Error('Response was not a PDF');
  }
  return buffer;
};

/**
 * Web port of students-app/hooks/useMergedPdf.ts
 * Downloads the answer sheet (evaluated PDF, then marksheet fallback), optionally
 * prepends the results sheet, and returns a blob URL for the PDF viewer.
 */
export const useMergedPdf = ({
  studentAssignmentId,
  answerSheetUrl,
  pdfKey,
  enabled,
  token
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!enabled) {
      setBlobUrl(null);
      setIsLoading(false);
      setErrorMessage('');
      return undefined;
    }

    let cancelled = false;
    let createdUrl = null;

    const load = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setBlobUrl(null);

      try {
        const candidates = [answerSheetUrl, getFileDownloadUrl(pdfKey)].filter(
          (url, index, list) => url && list.indexOf(url) === index
        );

        let answerBytes = null;
        let lastError = null;
        for (const url of candidates) {
          try {
            answerBytes = await fetchPdfBytes(url, token);
            if (answerBytes) break;
          } catch (error) {
            lastError = error;
          }
        }

        if (!answerBytes) {
          throw lastError || new Error('Answer sheet download failed');
        }

        if (cancelled) return;

        let finalBytes = new Uint8Array(answerBytes);

        const resultsUrl = getResultsSheetUrl(studentAssignmentId);
        if (resultsUrl && token) {
          try {
            const resultsBytes = await fetchPdfBytes(resultsUrl, token);
            if (resultsBytes) {
              finalBytes = await mergeResultsWithAnswerSheet(resultsBytes, answerBytes);
            }
          } catch (mergeError) {
            console.error('PDF merge failed, using answer sheet only:', mergeError);
          }
        }

        if (cancelled) return;

        const blob = new Blob([finalBytes], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error?.message || 'Failed to load document.');
          setBlobUrl(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [enabled, answerSheetUrl, pdfKey, studentAssignmentId, token]);

  return { blobUrl, isLoading, errorMessage };
};
